import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { AdminProjectImages } from './AdminProjectImages';
import { LayoutSelector } from './LayoutSelector';
import { ArrowLeft, Eye, EyeSlash } from '@phosphor-icons/react';

const monoFont = "'IBM Plex Mono', monospace";
const C = {
  fg: 'rgba(247,246,242,0.85)',
  fgMuted: 'rgba(247,246,242,0.45)',
  border: 'rgba(247,246,242,0.15)',
  published: '#00E87A',
};

interface AdminMenuGalleryProps {
  menuItemId: string;
  onBack: () => void;
}

export function AdminMenuGallery({ menuItemId, onBack }: AdminMenuGalleryProps) {
  const { session } = useAuth();
  const [label, setLabel] = useState('');
  const [savedLayout, setSavedLayout] = useState('A');
  const [pendingLayout, setPendingLayout] = useState('A');
  const [published, setPublished] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    const { data } = await supabase
      .from('menu_items')
      .select('label, layout_type, published')
      .eq('id', menuItemId)
      .single();
    if (data) {
      setLabel((data as any).label);
      const lt = (data as any).layout_type || 'A';
      setSavedLayout(lt);
      setPendingLayout(lt);
      setPublished((data as any).published ?? false);
    }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [menuItemId]);

  const applyLayout = async () => {
    await supabase.from('menu_items').update({ layout_type: pendingLayout } as any).eq('id', menuItemId);
    setSavedLayout(pendingLayout);
  };

  const togglePublish = async () => {
    const next = !published;
    await supabase.from('menu_items').update({ published: next } as any).eq('id', menuItemId);
    setPublished(next);
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  if (loading) {
    return <p style={{ fontFamily: monoFont, fontSize: 12, color: C.fgMuted }}>Carregando...</p>;
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} style={{ color: C.fgMuted, background: 'none', border: 'none', cursor: 'pointer' }}>
          <ArrowLeft size={18} weight="thin" />
        </button>
        <h2 className="uppercase text-sm font-light tracking-wider flex-1" style={{ fontFamily: monoFont, color: C.fg }}>
          {label}
        </h2>
        <button
          onClick={togglePublish}
          className="text-[11px] uppercase inline-flex items-center gap-1.5 py-1 px-3"
          style={{
            fontFamily: monoFont,
            border: '0.5px solid',
            borderColor: published ? C.published : C.border,
            color: published ? C.published : C.fgMuted,
            backgroundColor: 'transparent',
            cursor: 'pointer',
          }}
        >
          {published ? <><Eye size={12} weight="thin" /> Publicada</> : <><EyeSlash size={12} weight="thin" /> Rascunho</>}
        </button>
      </div>

      <LayoutSelector
        currentLayout={pendingLayout}
        onSelect={setPendingLayout}
        hasChange={pendingLayout !== savedLayout}
        onApply={applyLayout}
      />

      <AdminProjectImages mode="menu-gallery" menuItemId={menuItemId} />
    </div>
  );
}
